import { useEffect, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import Button from "../../componentes/button/button";
import Textarea from "../../componentes/input/textarea";
import { Loading } from "../../componentes/loading/loading";
import VersaoAppService from "../../services/resource/versaoAppService";
import { formatarDataHora } from "../../utils/solicitacoes/formatters";

const service = new VersaoAppService();

function formatarTamanho(bytes) {
    if (!bytes) return "-";
    return `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** Admin: publicação das versões do APK do app Mydash Workflows, que é distribuído fora da Play
 * Store — o app consulta a última versão publicada aqui e oferece a atualização ao usuário. */
export default function AdminVersoesApp() {
    const [versoes, setVersoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [arquivo, setArquivo] = useState(null);
    const [inputKey, setInputKey] = useState(0);
    const [notas, setNotas] = useState("");
    const [obrigatoria, setObrigatoria] = useState(false);
    const [progresso, setProgresso] = useState(null);
    const [baixandoId, setBaixandoId] = useState(null);
    const [paraExcluir, setParaExcluir] = useState(null);

    useEffect(() => {
        document.title = "MyDash | Versões do App";
        carregar();
    }, []);

    function carregar() {
        setLoading(true);
        service.listar()
            .then(res => setVersoes(res.data))
            .catch(() => setVersoes([]))
            .finally(() => setLoading(false));
    }

    async function publicar() {
        if (!arquivo) {
            toast.warning("Selecione o arquivo .apk.");
            return;
        }
        setProgresso(0);
        try {
            const res = await service.publicar(arquivo, notas, obrigatoria, setProgresso);
            toast.success(`Versão ${res.data.versionName} publicada.`);
            setArquivo(null);
            setInputKey(k => k + 1);
            setNotas("");
            setObrigatoria(false);
            carregar();
        } catch {
            // a mensagem do backend (ex.: versionCode não maior que o da última versão) já vira toast no interceptor da API
        } finally {
            setProgresso(null);
        }
    }

    async function baixar(versao) {
        setBaixandoId(versao.id);
        try {
            const res = await service.baixar(versao.id);
            saveAs(res.data, versao.nomeArquivo);
        } finally {
            setBaixandoId(null);
        }
    }

    function excluirConfirmado() {
        service.excluir(paraExcluir.id)
            .then(() => { toast.success(`Versão ${paraExcluir.versionName} excluída.`); carregar(); })
            .finally(() => setParaExcluir(null));
    }

    const publicando = progresso !== null;
    const ultima = versoes[0];

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto flex flex-col gap-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-foreground mb-1">Versões do App</h1>
                    <p className="text-sm text-muted-foreground">
                        Publique aqui cada novo APK do Mydash Workflows. O app verifica a última versão publicada ao ser aberto e oferece a atualização ao usuário.
                    </p>
                </div>

                <div className="bg-card border border-border rounded-2xl shadow-card p-4 md:p-6 flex flex-col gap-4">
                    <h2 className="font-display text-base font-semibold text-foreground">Publicar nova versão</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">
                                    Arquivo APK<span className="text-destructive ml-1">*</span>
                                </label>
                                <input
                                    key={inputKey}
                                    type="file"
                                    accept=".apk"
                                    disabled={publicando}
                                    onChange={e => setArquivo(e.target.files?.[0] || null)}
                                    className="border border-input rounded-md w-full px-3 py-2 text-sm"
                                />
                            </div>
                            <Textarea
                                label="Novidades desta versão"
                                placeholder="Opcional — aparece no aviso de atualização do app"
                                rows={4}
                                value={notas}
                                disabled={publicando}
                                onChange={e => setNotas(e.target.value)}
                            />
                            <label className="flex items-center gap-2 text-sm text-foreground/80">
                                <InputSwitch checked={obrigatoria} disabled={publicando} onChange={e => setObrigatoria(e.value)} />
                                Atualização obrigatória (o usuário não pode adiar)
                            </label>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="blue"
                                    iconLeft="fa fa-upload"
                                    label={publicando ? `Enviando... ${progresso}%` : "Publicar versão"}
                                    loading={publicando}
                                    disabled={publicando || !arquivo}
                                    onClick={publicar}
                                />
                            </div>
                        </div>
                        <div className="text-sm text-muted-foreground flex flex-col gap-2">
                            <p>
                                A <strong>versão é lida do próprio APK</strong> (versionCode e versionName definidos em <code>android/app/build.gradle</code>), não precisa ser digitada.
                            </p>
                            <p>
                                Só é aceito um APK com versionCode <strong>maior</strong> que o da última versão publicada
                                {ultima ? <> (atualmente <strong>{ultima.versionName}</strong>, versionCode {ultima.versionCode})</> : null}.
                            </p>
                            <p>
                                O APK precisa ser assinado com a <strong>mesma chave de release</strong> das versões anteriores — senão o Android recusa instalar por cima.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                    <div className="bg-primary px-4 py-3 md:px-7">
                        <h2 className="font-display text-lg font-bold text-primary-foreground">Versões publicadas</h2>
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <div className="overflow-x-auto fade-in">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header">
                                    <tr className="text-sm">
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Versão</th>
                                        <th className="py-3 px-6 text-center text-xs font-medium text-table-header-fg uppercase tracking-wider">versionCode</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Novidades</th>
                                        <th className="py-3 px-6 text-center text-xs font-medium text-table-header-fg uppercase tracking-wider">Tamanho</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Publicado</th>
                                        <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {versoes.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-muted-foreground">Nenhuma versão publicada</td>
                                        </tr>
                                    ) : (
                                        versoes.map((v, index) => (
                                            <tr key={v.id} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">
                                                    <div className="flex items-center gap-2">
                                                        {v.versionName}
                                                        {index === 0 && (
                                                            <span className="inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold bg-green/10 text-green">Atual</span>
                                                        )}
                                                        {v.obrigatoria && (
                                                            <span className="inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold bg-destructive/10 text-destructive">Obrigatória</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-center text-muted-foreground">{v.versionCode}</td>
                                                <td className="px-6 py-3 text-muted-foreground whitespace-pre-line">{v.notas || "-"}</td>
                                                <td className="px-6 py-3 whitespace-nowrap text-center text-muted-foreground">{formatarTamanho(v.tamanho)}</td>
                                                <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">
                                                    {formatarDataHora(v.dataPublicacao)}
                                                    {v.publicadoPorNome ? <span className="block">{v.publicadoPorNome}</span> : null}
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <i
                                                            onClick={() => baixandoId === null && baixar(v)}
                                                            title="Baixar APK"
                                                            className={`fa ${baixandoId === v.id ? "fa-spinner fa-spin" : "fa-download"} text-primary hover:text-primary/80 p-1 rounded-full hover:bg-primary/10 cursor-pointer`}
                                                        />
                                                        <i
                                                            onClick={() => setParaExcluir(v)}
                                                            title="Excluir versão"
                                                            className="fa fa-times text-destructive hover:text-destructive/80 p-1 rounded-full hover:bg-destructive/10 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {paraExcluir && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Você confirma a exclusão da versão {paraExcluir.versionName}?</p>
                        <p className="text-sm text-muted-foreground mt-2">
                            Quem já instalou continua com ela; os demais passam a ver a versão anterior como a mais recente.
                        </p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setParaExcluir(null)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={excluirConfirmado} className="px-4 py-2 rounded-md transition-all duration-150 bg-destructive text-destructive-foreground hover:brightness-95">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
